import { get } from 'lodash';
import qs from 'qs';
import server, { csrfFromDom } from 'common/utils/server';
import { translate } from 'common/utils/i18n';
import { followUpableTypes } from './followUps';
import { notifyError } from './notificationState';

const initialState = {
  solicitations: [],
  surveyQuestions: [],
  profanityFilter: '',
  propertyKeys: {},
  total: undefined,
  loading: true,
  error: false,
  reachedEnd: false,
};

function updateSolicitation(state, solicitation, newSolicitation) {
  return [...state.solicitations]
    .map(s => {
      if (solicitation.service_recovery) {
        return get(s, 'recovery_feedback.id') === solicitation.id
          ? { ...s, recovery_feedback: newSolicitation }
          : s;
      }
      return s.id === solicitation.id ? newSolicitation : s;
    })
    .filter(s => s !== null);
}

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case 'stream/APPEND_SOLICITATIONS': {
      const { solicitations } = action;

      return {
        ...state,
        solicitations: [...state.solicitations, ...solicitations],
      };
    }

    case 'stream/PREPEND_SOLICITATIONS': {
      const { solicitations } = action;

      return {
        ...state,
        solicitations: [...solicitations, ...state.solicitations],
      };
    }

    case 'stream/REMOVE_SOLICITATION': {
      const { solicitation } = action;

      return {
        ...state,
        solicitations: updateSolicitation(state, solicitation, null),
      };
    }

    case 'stream/SET_SOLICITATION_EDIT_MODE': {
      const { solicitation, value } = action;

      const key = 'editMode';

      return {
        ...state,
        solicitations: state.solicitations.map(s => {
          if (
            s.id !== solicitation.id &&
            get(s, 'recovery_feedback.id') !== solicitation.id
          ) {
            return s;
          }

          const newSolicitation = solicitation.service_recovery
            ? {
                ...s,
                recovery_feedback: {
                  ...s.recovery_feedback,
                  [key]: value,
                },
              }
            : {
                ...s,
                [key]: value,
              };

          return newSolicitation;
        }),
      };
    }

    case 'stream/REMOVE_SOLICITATION_COMMENTS': {
      const { solicitation } = action;

      const removedComments = {
        ...solicitation,
        comments: '',
        answer_comment: '',
        answers: solicitation.answers.map(answer => ({
          ...answer,
          comment: '',
        })),
      };

      return {
        ...state,
        solicitations: updateSolicitation(state, solicitation, removedComments),
      };
    }

    case 'stream/SET_SOLICITATIONS': {
      return {
        ...state,
        solicitations: action.solicitations,
      };
    }

    case 'stream/SET_STREAM_TOTAL': {
      return {
        ...state,
        total: action.total,
      };
    }

    case 'stream/CLEAR_SOLICITATIONS': {
      return {
        ...state,
        solicitations: [],
        total: undefined,
      };
    }

    case 'stream/SET_STREAM_CONTEXT': {
      return {
        ...state,
        surveyQuestions: action.surveyQuestions,
        profanityFilter: action.profanityFilter,
        propertyKeys: action.propertyKeys,
      };
    }

    case 'followUps/RECEIVED_FOLLOWUPS': {
      const { followUpableType, followUpableId, followUps } = action;
      if (followUpableType !== followUpableTypes.solicitation) {
        return state;
      }
      const solicitation = state.solicitations.find(
        s => s.id === followUpableId
      );
      const followUpIds = solicitation.follow_up_ids || [];
      const newFollowUpIds = [
        ...new Set([...followUpIds, ...followUps.map(f => f.id)]),
      ];
      const newSolicitation = {
        ...solicitation,
        follow_up_ids: newFollowUpIds,
      };
      return {
        ...state,
        solicitations: state.solicitations.map(s => {
          return s.id === followUpableId ? newSolicitation : s;
        }),
      };
    }
    case 'stream/SET_REACHED_END': {
      return {
        ...state,
        reachedEnd: action.reachedEnd,
      };
    }
    case 'DETERMINED_SERIALIZED_SOLICITATION_STREAM_FILTERS': {
      return {
        ...state,
        reachedEnd: false,
      };
    }
    case 'stream/SET_LOADING': {
      return {
        ...state,
        loading: action.loading,
      };
    }
    default:
      return state;
  }
};

export const fetchURL = (url, options = {}) =>
  fetch(url, {
    credentials: 'include',
    headers: {
      'X-CSRF-Token': csrfFromDom(),
      'Content-Type': 'application/json',
    },
    ...options,
  }).then(response => response.json());

class APIError extends Error {
  constructor(message, status = 500) {
    super(message);

    this.name = 'APIError';
    this.status = status;
  }
}

export const fetchStreamSolicitations = (
  serializedFilters,
  scorecardFilterMode
) => dispatch => {
  dispatch({ type: 'stream/SET_LOADING', loading: true });

  const controller = window.AbortController
    ? new window.AbortController()
    : { abort: () => {}, signal: undefined };
  const subscription = window.ReactInterop.cancelPrevious.subscribe(() =>
    controller.abort()
  );

  const url = scorecardFilterMode
    ? '/api/v1/stream?scorecard_filters=true'
    : '/api/v1/stream';
  return fetch(url, {
    signal: controller.signal,
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfFromDom(),
    },
    body: JSON.stringify(qs.parse(serializedFilters)),
  })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        throw new APIError(
          `API returned status ${response.status}`,
          response.status
        );
      }
    })
    .then(json => {
      dispatch({
        type: 'stream/APPEND_SOLICITATIONS',
        solicitations: json.solicitations,
      });
      dispatch({ type: 'stream/SET_LOADING', loading: false });
      return json;
    })
    .catch(e => {
      subscription.unsubscribe();
      dispatch({ type: 'stream/SET_LOADING', loading: false });
      if (e.name !== 'AbortError') {
        // TODO: handle errors via generic error-handling middleware and remove this
      }
      if (e.name === 'APIError') {
        switch (e.status) {
          case 401:
            dispatch(notifyError(translate('company.authError')));
            break;
          default:
            dispatch(notifyError(translate('company.error')));
        }
      }
    });
};

export const fetchSolicitationByUuid = uuid => dispatch => {
  dispatch({ type: 'stream/SET_LOADING', loading: true });

  return server
    .get(`/api/v1/stream/${uuid}`)
    .then(json => {
      dispatch({
        type: 'stream/SET_SOLICITATIONS',
        solicitations: [json.solicitation],
      });
      dispatch({ type: 'stream/SET_LOADING', loading: false });
      return json;
    })
    .catch(e => {
      if (e.name !== 'AbortError') {
        // TODO: handle errors via generic error-handling middleware and remove this
      }
      dispatch({ type: 'stream/SET_LOADING', loading: false });
    });
};


export const fetchStreamContext = ignoreQuestionVisibility => dispatch => {
  return server
    .get('/api/v1/stream/stream_context', {
      ignore_visible_on_stream: ignoreQuestionVisibility,
    })
    .then(json => {
      dispatch({
        type: 'stream/SET_STREAM_CONTEXT',
        surveyQuestions: json.survey_questions,
        profanityFilter: json.profanity_filter,
        propertyKeys: json.property_keys,
      });
    });
};
